import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import Dashboard from './components/pages/Dashboard';
import AcceptInvite from './components/groups/AcceptInvite';
import TermsOfService from './components/legal/TermsOfService';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import RecipeGrid from './components/recipes/RecipeGrid';
import RecipeDetail from './components/recipes/RecipeDetail';
import RecipeForm from './components/recipes/RecipeForm';
import AppLayout from './components/layout/AppLayout';
import PaymentSuccess from './components/payment/PaymentSuccess';
import PaymentCancel from './components/payment/PaymentCancel';
import AdminPricing from './components/admin/AdminPricing';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminSeedPhotos from './components/admin/AdminSeedPhotos';
import AdminMarketingGallery from './components/admin/AdminMarketingGallery';
import AdminGroupRecipes from './components/admin/AdminGroupRecipes';
import AdminGroupSetupContent from './components/admin/AdminGroupSetupContent';
import AdminUsersWithoutGroup from './components/admin/AdminUsersWithoutGroup';
import AdminMembersReport from './components/admin/AdminMembersReport';
import AdminGiftCodes from './components/admin/AdminGiftCodes';
import BuyGift from './components/gift/BuyGift';
import RedeemGift from './components/gift/RedeemGift';
import MealPlanner from './components/mealplanner/MealPlanner';
import HelpPage from './components/help/HelpPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <p style={styles.loadingText}>🍳 Loading...</p>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

// Public Route - redirect to dashboard if already logged in
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <p style={styles.loadingText}>🍳 Loading...</p>
      </div>
    );
  }

  if (!user) return children;
  // Redirect to the same place Register/Login's own post-auth navigate()
  // would go -- so no matter which one "wins" the race after a fresh
  // login/register, both land somewhere correct instead of a dead end.
  return <Navigate to={inviteToken ? `/invite/${inviteToken}` : '/dashboard'} />;
};

// Admin Route - requires is_admin. The real enforcement is server-side
// (requireAdmin middleware); this just avoids showing the page in the UI.
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <p style={styles.loadingText}>🍳 Loading...</p>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  return user.is_admin ? children : <Navigate to="/dashboard" />;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" />} />

      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route path="/reset-password/:token" element={<ResetPassword />} />

      {/* Invite acceptance - accessible whether logged in or not */}
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes"
        element={
          <ProtectedRoute>
            <AppLayout><RecipeGrid /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes/new"
        element={
          <ProtectedRoute>
            <AppLayout><RecipeForm /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes/:id"
        element={
          <ProtectedRoute>
            <AppLayout><RecipeDetail /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes/:id/edit"
        element={
          <ProtectedRoute>
            <AppLayout><RecipeForm /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/cancel"
        element={
          <ProtectedRoute>
            <PaymentCancel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meal-planner"
        element={
          <ProtectedRoute>
            <AppLayout><MealPlanner /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <AppLayout><HelpPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/buy-gift"
        element={
          <ProtectedRoute>
            <AppLayout><BuyGift /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/redeem-gift"
        element={
          <ProtectedRoute>
            <AppLayout><RedeemGift /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AppLayout><AdminDashboard /></AppLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/pricing"
        element={
          <AdminRoute>
            <AppLayout><AdminPricing /></AppLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/seed-photos"
        element={
          <AdminRoute>
            <AppLayout><AdminSeedPhotos /></AppLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/marketing-gallery"
        element={
          <AdminRoute>
            <AppLayout><AdminMarketingGallery /></AppLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/groups/:groupId/recipes"
        element={
          <AdminRoute>
            <AppLayout><AdminGroupRecipes /></AppLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/group-setup-content"
        element={
          <AdminRoute>
            <AppLayout><AdminGroupSetupContent /></AppLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/users-without-group"
        element={
          <AdminRoute>
            <AppLayout><AdminUsersWithoutGroup /></AppLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/members-report"
        element={
          <AdminRoute>
            <AppLayout><AdminMembersReport /></AppLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/gift-codes"
        element={
          <AdminRoute>
            <AppLayout><AdminGiftCodes /></AppLayout>
          </AdminRoute>
        }
      />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
};

const styles = {
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
  },
  loadingText: {
    fontSize: '24px',
    color: '#e67e22',
  },
};

export default App;